/**
 * Exordium local-first sync engine.
 *
 * An on-device IndexedDB store per owner-scoped entity is the client's read/write source, so the
 * start page paints instantly and every feature works offline. A background sync reconciles with the
 * server:
 *
 *   pull  GET /1.0/Sync/Pull  -> the full live set; merge newest-wins, but never clobber a local row
 *                               that has an unpushed edit (_dirty). Deletions propagate by ABSENCE:
 *                               a previously-synced local row (_synced) that is no longer in the
 *                               pulled set was deleted elsewhere, so it is removed locally.
 *   push  POST /1.0/Sync/Push -> every _dirty row (creates/updates/deletes by GUID). On success the
 *                               reconciled rows (with server ids) replace the local copies and the
 *                               dirty flag clears; a pushed delete removes the local row.
 *
 * New records get a client-generated GUID, which is the stable identity across devices (the server
 * honors it on create). Records carry two client-only flags, never sent: _dirty (unpushed change)
 * and _synced (has been confirmed on the server / came from a pull).
 */

const ENTITIES =
[
	{ name: 'LinkGroup', guid: 'GUIDLinkGroup' },
	{ name: 'Link', guid: 'GUIDLink' },
	{ name: 'Note', guid: 'GUIDNote' },
	{ name: 'Drawing', guid: 'GUIDDrawing' },
	{ name: 'Theme', guid: 'GUIDTheme' },
	{ name: 'Background', guid: 'GUIDBackground' },
	{ name: 'VisitEvent', guid: 'GUIDVisitEvent' },
	{ name: 'Setting', guid: 'GUIDSetting' },
	{ name: 'Category', guid: 'GUIDCategory' },
	{ name: 'Interest', guid: 'GUIDInterest' },
	{ name: 'CategoryTag', guid: 'GUIDCategoryTag' }
];

const DB_NAME = 'exordium';
// Bump on every ENTITIES change so onupgradeneeded fires on existing clients and the new object
// stores are created; existing stores are left untouched. v2 added Drawing; v3 adds Category,
// Interest, and CategoryTag (the interest-graph tables).
const DB_VERSION = 3;

function nowStamp()
{
	return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

class ExordiumSync
{
	constructor(pOptions)
	{
		pOptions = pOptions || {};
		this.apiPrefix = pOptions.APIPrefix || '/1.0/';
		this.onChange = (typeof pOptions.onChange === 'function') ? pOptions.onChange : function () {};
		// Auth transport. The web app leaves these unset and keeps cookie/session auth
		// (credentials:'include'). A host that authenticates with a bearer token (the browser
		// extension) passes authHeader (e.g. 'Bearer <token>') and/or fetchOptions; when either is
		// present the engine sends the header and DROPS the cookie, because a cookie beats a bearer
		// token server-side in this stack. authHeader may be a string or a function returning the
		// current header value (so a token can change without rebuilding the engine).
		this.authHeader = pOptions.authHeader || pOptions.AuthHeader || null;
		this.fetchOptions = pOptions.fetchOptions || pOptions.FetchOptions || null;
		this._db = null;
		this._syncing = false;
		this._pending = false;
	}

	// Resolve the configured auth header to its current string value (supports a function form).
	_authHeaderValue()
	{
		let tmpHeader = this.authHeader;
		if (typeof tmpHeader === 'function') { try { tmpHeader = tmpHeader(); } catch (pErr) { tmpHeader = null; } }
		return (typeof tmpHeader === 'string' && tmpHeader) ? tmpHeader : null;
	}

	// Build the fetch init for a sync request. Default: send the session cookie (web app). When an
	// auth header or fetchOptions is configured: merge them in, add Authorization, and omit the
	// cookie so the bearer token is the only credential the server sees.
	_fetchInit(pInit)
	{
		let tmpInit = Object.assign({}, pInit);
		let tmpHeaders = Object.assign({}, (pInit && pInit.headers) || {});
		let tmpUseAuth = false;
		if (this.fetchOptions)
		{
			Object.keys(this.fetchOptions).forEach((pKey) => { if (pKey !== 'headers') { tmpInit[pKey] = this.fetchOptions[pKey]; } });
			if (this.fetchOptions.headers) { Object.assign(tmpHeaders, this.fetchOptions.headers); tmpUseAuth = true; }
		}
		let tmpAuth = this._authHeaderValue();
		if (tmpAuth) { tmpHeaders.Authorization = tmpAuth; tmpUseAuth = true; }
		tmpInit.headers = tmpHeaders;
		// Respect an explicit credentials choice from fetchOptions; otherwise pick by auth mode.
		if (typeof tmpInit.credentials === 'undefined') { tmpInit.credentials = tmpUseAuth ? 'omit' : 'include'; }
		return tmpInit;
	}

	guidField(pEntity) { let tmpE = ENTITIES.filter((e) => e.name === pEntity)[0]; return tmpE ? tmpE.guid : null; }
	newGUID() { return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : ('x' + Date.now() + '-' + Math.round(Math.random() * 1e9)); }

	open()
	{
		let tmpSelf = this;
		return new Promise((fResolve, fReject) =>
		{
			let tmpRequest = indexedDB.open(DB_NAME, DB_VERSION);
			tmpRequest.onupgradeneeded = (pEvent) =>
			{
				let tmpDB = pEvent.target.result;
				ENTITIES.forEach((pEnt) => { if (!tmpDB.objectStoreNames.contains(pEnt.name)) { tmpDB.createObjectStore(pEnt.name, { keyPath: pEnt.guid }); } });
				if (!tmpDB.objectStoreNames.contains('meta')) { tmpDB.createObjectStore('meta', { keyPath: 'key' }); }
			};
			tmpRequest.onsuccess = (pEvent) =>
			{
				tmpSelf._db = pEvent.target.result;
				if (typeof window !== 'undefined' && window.addEventListener) { window.addEventListener('online', () => tmpSelf.sync()); }
				fResolve(tmpSelf);
			};
			tmpRequest.onerror = () => fReject(tmpRequest.error);
		});
	}

	_store(pName, pMode) { return this._db.transaction(pName, pMode || 'readonly').objectStore(pName); }

	_all(pName)
	{
		let tmpSelf = this;
		return new Promise((fResolve) =>
		{
			let tmpOut = [];
			let tmpRequest = tmpSelf._store(pName).openCursor();
			tmpRequest.onsuccess = (pEvent) => { let tmpCursor = pEvent.target.result; if (tmpCursor) { tmpOut.push(tmpCursor.value); tmpCursor.continue(); } else { fResolve(tmpOut); } };
			tmpRequest.onerror = () => fResolve(tmpOut);
		});
	}
	_get(pName, pGUID) { let tmpSelf = this; return new Promise((fResolve) => { let r = tmpSelf._store(pName).get(pGUID); r.onsuccess = () => fResolve(r.result || null); r.onerror = () => fResolve(null); }); }
	_put(pName, pRecord) { let tmpSelf = this; return new Promise((fResolve, fReject) => { let r = tmpSelf._store(pName, 'readwrite').put(pRecord); r.onsuccess = () => fResolve(pRecord); r.onerror = () => fReject(r.error); }); }
	_del(pName, pGUID) { let tmpSelf = this; return new Promise((fResolve) => { let r = tmpSelf._store(pName, 'readwrite').delete(pGUID); r.onsuccess = () => fResolve(true); r.onerror = () => fResolve(false); }); }

	// Live records for rendering (locally- or remotely-deleted rows excluded).
	getAll(pEntity) { return this._all(pEntity).then((pRows) => pRows.filter((r) => !r.Deleted)); }

	// Create or update a record locally; marks it dirty and schedules a push.
	save(pEntity, pRecord)
	{
		let tmpSelf = this;
		let tmpGUIDField = this.guidField(pEntity);
		let tmpRecord = Object.assign({}, pRecord);
		if (!tmpRecord[tmpGUIDField]) { tmpRecord[tmpGUIDField] = this.newGUID(); }
		tmpRecord._dirty = true;
		tmpRecord.UpdateDate = nowStamp();
		if (!tmpRecord.CreateDate) { tmpRecord.CreateDate = tmpRecord.UpdateDate; }
		return this._put(pEntity, tmpRecord).then((r) => { tmpSelf._kick(); return r; });
	}

	// Delete a record locally (tombstone + dirty); the push turns it into a server soft-delete.
	remove(pEntity, pGUID)
	{
		let tmpSelf = this;
		return this._get(pEntity, pGUID).then((pRecord) =>
		{
			if (!pRecord) { return null; }
			pRecord.Deleted = 1; pRecord._dirty = true; pRecord.UpdateDate = nowStamp();
			return tmpSelf._put(pEntity, pRecord).then((r) => { tmpSelf._kick(); return r; });
		});
	}

	_kick() { let tmpSelf = this; if (typeof navigator === 'undefined' || navigator.onLine) { setTimeout(() => tmpSelf.sync(), 60); } }

	sync()
	{
		let tmpSelf = this;
		if (typeof navigator !== 'undefined' && !navigator.onLine) { return Promise.resolve(false); }
		if (this._syncing) { this._pending = true; return Promise.resolve(false); }
		this._syncing = true;
		return this._getGeneration().then((pStoredGen) =>
		{
			// A normal delta pull; it reports the server's data generation. A CHANGED generation means
			// the server store was reset/restored, so reconcile (drop rows the server no longer has)
			// instead of accumulating orphans. Reconcile runs ONLY on a generation change, so an
			// ordinary sync (or a server hiccup) can never trigger a destructive absence-removal.
			return tmpSelf._pull().then((pServerGen) =>
			{
				if (pServerGen && pServerGen !== pStoredGen) { return tmpSelf._reconcile(pServerGen); }
				return null;
			});
		}).then(() => tmpSelf._push()).then(() =>
		{
			tmpSelf._syncing = false;
			tmpSelf.onChange();
			if (tmpSelf._pending) { tmpSelf._pending = false; return tmpSelf.sync(); }
			return true;
		}).catch(() => { tmpSelf._syncing = false; return false; });
	}

	_getCursor() { let tmpSelf = this; return new Promise((fResolve) => { let r = tmpSelf._store('meta').get('cursor'); r.onsuccess = () => fResolve(r.result ? r.result.value : ''); r.onerror = () => fResolve(''); }); }
	_setCursor(pValue) { let tmpSelf = this; return new Promise((fResolve) => { let r = tmpSelf._store('meta', 'readwrite').put({ key: 'cursor', value: pValue || '' }); r.onsuccess = () => fResolve(true); r.onerror = () => fResolve(true); }); }
	// The server data generation this store last reconciled against (see _reconcile). '' until the first sync.
	_getGeneration() { let tmpSelf = this; return new Promise((fResolve) => { let r = tmpSelf._store('meta').get('generation'); r.onsuccess = () => fResolve(r.result ? r.result.value : ''); r.onerror = () => fResolve(''); }); }
	_setGeneration(pValue) { let tmpSelf = this; return new Promise((fResolve) => { let r = tmpSelf._store('meta', 'readwrite').put({ key: 'generation', value: pValue || '' }); r.onsuccess = () => fResolve(true); r.onerror = () => fResolve(true); }); }

	// Changed-since delta: rows with UpdateDate >= cursor, including tombstones (a soft-delete bumps
	// UpdateDate). No cursor => full sync. ServerTime becomes the next cursor. Returns the server's data
	// generation (or null) so sync() can decide whether a reconcile is needed.
	_pull()
	{
		let tmpSelf = this;
		return this._getCursor().then((pCursor) =>
		{
			let tmpURL = tmpSelf.apiPrefix + 'Sync/Pull' + (pCursor ? ('?since=' + encodeURIComponent(pCursor)) : '');
			return fetch(tmpURL, tmpSelf._fetchInit({ headers: { Accept: 'application/json' } }))
				.then((pResponse) => { if (!pResponse.ok) { throw new Error('pull ' + pResponse.status); } return pResponse.json(); })
				.then((pData) =>
				{
					// Never act on a partial/malformed 200 (a well-formed response always carries ServerTime).
					if (!pData || !pData.ServerTime) { throw new Error('malformed pull response'); }
					let tmpChain = Promise.resolve();
					ENTITIES.forEach((pEnt) => { tmpChain = tmpChain.then(() => tmpSelf._applyPulled(pEnt, Array.isArray(pData[pEnt.name]) ? pData[pEnt.name] : [])); });
					return tmpChain.then(() => tmpSelf._setCursor(pData.ServerTime || pCursor)).then(() => (pData.Generation || null));
				});
		});
	}

	// Apply a delta batch: upsert the live rows (unless a local unpushed edit exists), remove the
	// tombstoned ones (unless we have an unpushed local edit that should win and re-push).
	_applyPulled(pEnt, pServerRows)
	{
		let tmpSelf = this;
		let tmpChain = Promise.resolve();
		pServerRows.forEach((pServer) =>
		{
			tmpChain = tmpChain.then(() => tmpSelf._get(pEnt.name, pServer[pEnt.guid]).then((pLocal) =>
			{
				if (pServer.Deleted)
				{
					if (!pLocal || !pLocal._dirty) { return tmpSelf._del(pEnt.name, pServer[pEnt.guid]); }
					return null;
				}
				if (pLocal && pLocal._dirty) { return null; }
				return tmpSelf._put(pEnt.name, Object.assign({}, pServer, { _dirty: false, _synced: true }));
			}));
		});
		return tmpChain;
	}

	// Reconcile after a DETECTED server reset (the data generation changed). Pull the ENTIRE owner set
	// (no cursor), apply it, then remove local rows the server no longer has -- but NEVER an unpushed
	// local edit (_dirty), and only rows that were previously confirmed on the server (_synced). Any
	// malformed/partial response aborts before deleting anything, so this cannot lose data to a hiccup.
	// The new generation is recorded only after the whole pass succeeds, so a failure just retries.
	_reconcile(pGen)
	{
		let tmpSelf = this;
		return fetch(tmpSelf.apiPrefix + 'Sync/Pull', tmpSelf._fetchInit({ headers: { Accept: 'application/json' } }))
			.then((pResponse) => { if (!pResponse.ok) { throw new Error('reconcile pull ' + pResponse.status); } return pResponse.json(); })
			.then((pData) =>
			{
				if (!pData || !pData.ServerTime) { throw new Error('malformed reconcile response'); }
				let tmpChain = Promise.resolve();
				ENTITIES.forEach((pEnt) =>
				{
					let tmpServerRows = Array.isArray(pData[pEnt.name]) ? pData[pEnt.name] : [];
					let tmpServerGUIDs = {};
					tmpServerRows.forEach((r) => { if (r && r[pEnt.guid]) { tmpServerGUIDs[r[pEnt.guid]] = true; } });
					tmpChain = tmpChain
						.then(() => tmpSelf._applyPulled(pEnt, tmpServerRows))
						.then(() => tmpSelf._all(pEnt.name))
						.then((pLocalRows) =>
						{
							let tmpInner = Promise.resolve();
							pLocalRows.forEach((r) =>
							{
								if (r._dirty) { return; }                     // unpushed local data: never remove
								if (!r._synced) { return; }                   // never confirmed on the server: leave it
								if (tmpServerGUIDs[r[pEnt.guid]]) { return; } // still known to the server
								tmpInner = tmpInner.then(() => tmpSelf._del(pEnt.name, r[pEnt.guid])); // orphan of a reset
							});
							return tmpInner;
						});
				});
				return tmpChain.then(() => tmpSelf._setCursor(pData.ServerTime)).then(() => tmpSelf._setGeneration(pGen));
			});
	}

	_push()
	{
		let tmpSelf = this;
		let tmpBody = {};
		let tmpChain = Promise.resolve();
		ENTITIES.forEach((pEnt) =>
		{
			tmpBody[pEnt.name] = [];
			tmpChain = tmpChain.then(() => tmpSelf._all(pEnt.name).then((pRows) =>
			{
				pRows.filter((r) => r._dirty).forEach((r) =>
				{
					let tmpOut = Object.assign({}, r);
					delete tmpOut._dirty; delete tmpOut._synced;
					tmpBody[pEnt.name].push(tmpOut);
				});
			}));
		});
		return tmpChain.then(() =>
		{
			let tmpHasAny = ENTITIES.some((e) => tmpBody[e.name].length);
			if (!tmpHasAny) { return; }
			return fetch(tmpSelf.apiPrefix + 'Sync/Push', tmpSelf._fetchInit({ method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(tmpBody) }))
				.then((pResponse) => { if (!pResponse.ok) { throw new Error('push ' + pResponse.status); } return pResponse.json(); })
				.then((pResult) =>
				{
					let tmpApply = Promise.resolve();
					ENTITIES.forEach((pEnt) =>
					{
						let tmpRows = Array.isArray(pResult[pEnt.name]) ? pResult[pEnt.name] : [];
						tmpRows.forEach((pServer) =>
						{
							if (pServer.Deleted) { tmpApply = tmpApply.then(() => tmpSelf._del(pEnt.name, pServer[pEnt.guid])); }
							else { let tmpRecord = Object.assign({}, pServer, { _dirty: false, _synced: true }); tmpApply = tmpApply.then(() => tmpSelf._put(pEnt.name, tmpRecord)); }
						});
					});
					return tmpApply;
				});
		});
	}

	// ---- history rules (shared by the web app AND the extension) --------------
	// The owner Setting `history.rules` shapes what page visits are recorded and shown. Both the app
	// (window.ExordiumClient.Sync) and the extension (the vendored standalone) evaluate visits through
	// THESE helpers, so the shape and the matching logic have exactly one definition. Add new rule fields
	// here (normalizeHistoryRules) and new match logic here (isVisitDenied), then rebuild + re-vendor.

	static _denyHost(pURL)
	{
		try { return new URL(pURL).host.replace(/^www\./, '').toLowerCase(); } catch (pErr) { return ''; }
	}

	// Normalize a parsed history.rules object to the full shape, with defaults for anything missing.
	static normalizeHistoryRules(pParsed)
	{
		let tmpParsed = pParsed || {};
		let fArr = (pValue) => Array.isArray(pValue) ? pValue : [];
		return {
			Enabled: tmpParsed.Enabled !== false,
			MaxItems: parseInt(tmpParsed.MaxItems, 10) || 10,
			RecencyDays: parseInt(tmpParsed.RecencyDays, 10) || 30,
			// Ignore the host exactly, or as a subdomain of it (example.com covers www.example.com).
			DenyDomains: fArr(tmpParsed.DenyDomains),
			// Ignore if any of these strings appears anywhere in the host.
			DenyDomainContains: fArr(tmpParsed.DenyDomainContains),
			// Ignore if any of these strings appears anywhere in the full URL (path + query included).
			DenyURLContains: fArr(tmpParsed.DenyURLContains),
			// Opt-in: surface frequently/recently visited history pages as "interesting" candidates.
			SuggestInteresting: tmpParsed.SuggestInteresting === true,
			SuggestMinVisits: parseInt(tmpParsed.SuggestMinVisits, 10) || 3
		};
	}

	// True when a visit to pURL should NOT be recorded, per the rules. Case-insensitive throughout.
	static isVisitDenied(pURL, pRules)
	{
		let tmpRules = pRules || {};
		let tmpHost = ExordiumSync._denyHost(pURL);
		let tmpURL = String(pURL || '').toLowerCase();
		let fClean = (pStr) => String(pStr || '').trim().toLowerCase();
		let tmpDomainHit = (tmpRules.DenyDomains || []).some((pDomain) =>
		{
			let tmpD = fClean(pDomain);
			return !!tmpD && (tmpHost === tmpD || (tmpHost.length > tmpD.length && tmpHost.slice(-(tmpD.length + 1)) === ('.' + tmpD)));
		});
		if (tmpDomainHit) { return true; }
		if ((tmpRules.DenyDomainContains || []).some((pStr) => { let s = fClean(pStr); return !!s && tmpHost.indexOf(s) > -1; })) { return true; }
		if ((tmpRules.DenyURLContains || []).some((pStr) => { let s = fClean(pStr); return !!s && tmpURL.indexOf(s) > -1; })) { return true; }
		return false;
	}
}

ExordiumSync.HISTORY_RULES_KEY = 'history.rules';

module.exports = ExordiumSync;
module.exports.ENTITIES = ENTITIES;
